/**
 * Which programs the teacher portal shows, and which calendar day a program's
 * attendance is taken against.
 *
 * The portal used to be a summer-camp tool: every page selected `type = 'camp'` and then
 * name-matched `/^Week \d+:/`, and attendance assumed a camp runs every day between its
 * start and end dates. Regular programs break both assumptions — an after-school program
 * runs Mon–Fri for ten months around school closures, and an enrichment class meets on a
 * single weekday. Program selection is now type-based, and the meeting day comes from
 * `schedule_cron` plus the school's blackout calendar.
 */

import { DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../config/brands';
import {
  closureLabelForProgramType,
  isClosedForProgramType,
} from '../utils/programCalendar';
import { getSessionWeekdays, shiftDateString, weekdayOfDateString } from '../utils/schedule';

/**
 * Re-exported so every Teacher Portal page keeps a single import for what it must filter.
 * The list itself lives in `utils/programCalendar`, shared with the parent dashboard: a
 * waitlist has no sessions on anyone's screen, not just a teacher's.
 */
export { NON_TEACHING_PROGRAM_TYPES, NON_TEACHING_TYPES_FILTER } from '../utils/programCalendar';

/**
 * Re-exported so the teacher portal keeps one import for everything session resolution
 * needs. The list and the routing rule live in `utils/programCalendar`, shared with the
 * parent dashboard so the two surfaces cannot disagree about whether a class meets.
 */
export { ENRICHMENT_PROGRAM_TYPES } from '../utils/programCalendar';

/** The program fields session resolution needs. */
export interface SessionProgram {
  type: string;
  schedule_cron: string;
  /** DATE column, `YYYY-MM-DD`. */
  start_date: string;
  /** DATE column, `YYYY-MM-DD`. */
  end_date: string;
  /**
   * Days this program specifically does not meet, beyond the school-wide calendar —
   * `programs.excluded_dates`, YYYY-MM-DD.
   *
   * The escape hatch, not the main axis: nearly every no-class date across the current
   * twelve programs is school-wide and comes from `blackoutDates.ts`. What is left is a
   * vendor taking its own day off while the building runs normally — Stanford Park Sports
   * skipping Fri 2026-10-02 when chess meets that same afternoon. Nothing but a per-program
   * list can express that.
   */
  excluded_dates?: string[] | null;
  /**
   * Which brand's closure calendar this program keeps — `programs.brand_key`.
   *
   * Optional because rows selected before the column existed omit it; absent reads as the
   * default brand, matching the column's own DEFAULT. Without it a teacher's roster for a
   * Kaleido class is closed by a BHCS holiday.
   */
  brand_key?: string | null;
}

/** The closure calendar a program keeps; anything unrecognized reads as the default brand. */
function brandOf(program: SessionProgram): BrandKey {
  return isBrandKey(program.brand_key) ? program.brand_key : DEFAULT_BRAND_KEY;
}

/** Why attendance can't be recorded right now. `null` when it can. */
export type SessionBlocker = 'before_start' | 'after_end' | 'no_session_today';

export interface ResolvedSession {
  /** The calendar day the roster displays and records against. */
  date: string;
  /** True only when today is a live meeting day inside the program's date range. */
  canRecord: boolean;
  blocker: SessionBlocker | null;
  /** School-closure reason when today is blacked out, e.g. "BHCS Closed — Thanksgiving". */
  closureLabel?: string;
}

/**
 * Whether the program actually meets on `ymd`: inside its date range, on one of its cron
 * weekdays, not a closure for *its* audience, and not one of its own excluded dates.
 *
 * Which calendar applies is the whole subtlety. Camps run on the very days that close
 * after-school, so they consult neither list. Enrichment classes are cancelled by a morning
 * camp or Halloween that leaves after-school open, so they ask `isEnrichmentClosed`.
 * Everything else asks `isBlackoutDate`, which still means exactly "after-school does not
 * run" and is unchanged by any of this.
 */
export function isSessionDate(program: SessionProgram, ymd: string): boolean {
  // DATE strings in YYYY-MM-DD compare correctly as plain strings.
  if (ymd < program.start_date || ymd > program.end_date) return false;
  if (!getSessionWeekdays(program.schedule_cron).has(weekdayOfDateString(ymd))) return false;
  // A camp is what the closure calendar is closing *for*; its own dates are the answer.
  if (program.type === 'camp') return true;
  if (program.excluded_dates?.includes(ymd)) return false;
  return !isClosedForProgramType(program.type, ymd, brandOf(program));
}

/**
 * The reason this program is not meeting today, or undefined when the day is ordinary.
 *
 * Reads the same calendar `isSessionDate` used, so the roster cannot refuse to record a
 * session and then explain it with the wrong holiday — or, worse, name a closure that
 * applies to somebody else's program.
 */
function closureLabelFor(program: SessionProgram, ymd: string): string | undefined {
  return closureLabelForProgramType(program.type, ymd, brandOf(program));
}

/**
 * Longest run of consecutive non-session days worth scanning past. BHCS's winter break is
 * the widest real gap (~2.5 weeks); 45 days clears it with room to spare, and the scan
 * terminates early anyway once it walks outside the program's date range.
 */
const SESSION_SEARCH_DAYS = 45;

function findSession(program: SessionProgram, from: string, step: 1 | -1): string | null {
  let date = from;
  for (let i = 0; i <= SESSION_SEARCH_DAYS; i++) {
    if (isSessionDate(program, date)) return date;
    date = shiftDateString(date, step);
  }
  return null;
}

/** First meeting day on or after `from`, or null if there isn't one in range. */
export function nextSessionDate(program: SessionProgram, from: string): string | null {
  return findSession(program, from, 1);
}

/**
 * The first day after `today` on which any of `programs` meets, or null when none does
 * within the search window.
 *
 * What the teacher portal falls forward to on a day nothing meets: a Saturday opens on
 * Monday's roster rather than a blank screen, and a teacher whose only class is Wednesday
 * chess sees Wednesday's children.
 */
export function nextSessionDateAcross(
  programs: readonly SessionProgram[],
  today: string,
): string | null {
  const from = shiftDateString(today, 1);
  let earliest: string | null = null;
  for (const program of programs) {
    const next = nextSessionDate(program, from);
    if (next && (earliest === null || next < earliest)) earliest = next;
  }
  return earliest;
}

/** Most recent meeting day on or before `from`, or null if there isn't one in range. */
export function previousSessionDate(program: SessionProgram, from: string): string | null {
  return findSession(program, from, -1);
}

/**
 * The program's most recent meeting days on or before `today`, oldest first, capped at
 * `limit`.
 *
 * Deriving the dates from the schedule lets attendance summaries query only the days a
 * program actually met, instead of pulling every record it has ever produced — which for a
 * ten-month after-school program is tens of thousands of rows.
 */
export function recentSessionDates(
  program: SessionProgram,
  today: string,
  limit: number,
): string[] {
  const dates: string[] = [];
  let cursor = today < program.end_date ? today : program.end_date;
  while (dates.length < limit) {
    const found = previousSessionDate(program, cursor);
    if (!found) break;
    dates.push(found);
    cursor = shiftDateString(found, -1);
  }
  return dates.reverse();
}

/**
 * Resolve which day the roster should show, and whether check-in is live.
 *
 * Recording is allowed on exactly one day — today, and only when today is a real meeting
 * day. Every other case falls back to a nearby session for read-only context rather than
 * seeding attendance rows against a day the program never met.
 */
export function resolveSession(
  program: SessionProgram | null | undefined,
  today: string,
): ResolvedSession {
  if (!program) return { date: today, canRecord: false, blocker: null };

  if (isSessionDate(program, today)) {
    return { date: today, canRecord: true, blocker: null };
  }

  if (today < program.start_date) {
    return {
      date: nextSessionDate(program, program.start_date) ?? program.start_date,
      canRecord: false,
      blocker: 'before_start',
    };
  }

  if (today > program.end_date) {
    return {
      date: previousSessionDate(program, program.end_date) ?? program.end_date,
      canRecord: false,
      blocker: 'after_end',
    };
  }

  // In range, but the program doesn't meet today — wrong weekday, or a school closure.
  // Show the most recent session so the teacher can still review it.
  return {
    date: previousSessionDate(program, today) ?? nextSessionDate(program, today) ?? today,
    canRecord: false,
    blocker: 'no_session_today',
    closureLabel: closureLabelFor(program, today),
  };
}
